/* Copyright (c) 2020-present CloudIO Inc */

import { useStateXSelector } from '@cloudio-saas/statex';
import {
  Button,
  DialogActions,
  InputAdornment,
  TextField,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { TypescriptProp } from 'componentregistry';
import { DialogPopup } from 'components/Popup/DialogPopup';
import Controller from 'controller/Controller';
import CodeInput from 'custom/CodeInput';
import { helpTextSelector } from 'designer/state';
import useComponentSize from 'hooks/useComponentSize';
import Icon from 'icons/Icon';
import { usePlatform } from 'platform/PlatformHooks';
import { memo, useEffect, useState } from 'react';
import { useTheme } from 'theme/Theme';
import { useControllerCodeEditorTypes } from '../../DataSourceView/CodeEditorTypes';
import { getDefaultValues, isRequired } from './PropertyEditorUtils';
import { PropertyEditorProps } from './types';

const useStyles = makeStyles({
  root: {
    marginTop: 8,
    '&:first-child': {
      marginTop: 0,
    },
  },
  input: {
    '& input, textarea': {
      fontSize: '0.8rem',
    },
  },
  tip: {
    cursor: 'default',
  },
});

export function TypescriptDialog({
  label,
  value,
  onSubmit,
  onClose,
  preCompile,
  compileOnChange,
}: {
  label: string;
  value: string;
  onSubmit: (val: string, editing?: boolean) => void;
  onClose: () => void;
  preCompile?: (script: string) => string;
  compileOnChange?: boolean;
}) {
  const [script, setScript] = useState(value);
  const platform = usePlatform();
  const theme = useTheme();
  const s = useComponentSize();
  useControllerCodeEditorTypes();

  useEffect(() => {
    if (compileOnChange && script !== value) {
      async function run() {
        let source = script;
        if (preCompile) {
          source = preCompile(source);
        }
        if (
          await Controller.compile({
            appUid: 'cloudio',
            pageId: 'typescript_instant_compile',
            platform,
            source,
            fileName: 'typescript_test.tsx',
            debug: true,
            instant: true,
          })
        ) {
          onSubmit(script, true);
        }
      }
      run();
    }
  }, [compileOnChange, onSubmit, platform, preCompile, script, value]);

  const header = <>{label}</>;

  const bodyChildren = (
    <CodeInput
      darkMode={theme.palette.mode !== 'light'}
      height={s.height}
      width={s.width}
      value={script ?? ''}
      onChange={setScript}
      language="typescript"
    />
  );

  const popupActions = (
    <DialogActions>
      <Button
        startIcon={<Icon parentColor="primary" iconName="check" />}
        onClick={async () => {
          let source = script;
          if (preCompile) {
            source = preCompile(source);
          }
          if (
            await Controller.compile({
              appUid: 'cloudio',
              pageId: 'typescript_test',
              platform,
              source,
              fileName: 'typescript_test.tsx',
              debug: true,
            })
          ) {
            onSubmit(script);
          }
        }}
        variant="contained"
      >
        Submit
      </Button>
    </DialogActions>
  );

  return (
    <DialogPopup
      platform={platform}
      Header={header}
      appUid={'cloudio'}
      bodyChildren={bodyChildren}
      itemId="send-test-email"
      onClose={onClose}
      open={true}
      pageId="$sidebar$"
      popupActions={popupActions}
      popupHeight={Math.min(window.innerHeight - 80, 600)}
      popupWidth={Math.min(window.innerWidth - 80, 900)}
      modal={false}
      devMode={false}
      disableEnforceFocus={true}
      hideBackdrop={true}
    />
  );
}

function TypescriptProperty({
  appUid,
  activeItemId,
  def,
  mgr,
  pageId,
  property,
  value = getDefaultValues<string>(mgr, property),
  onChange,
  platform,
  isViewOnlyUser = false,
}: PropertyEditorProps<TypescriptProp, string>) {
  const classes = useStyles();
  const [edit, setEdit] = useState(false);
  const helpText = useStateXSelector(helpTextSelector, {
    params: { appUid, pageId, itemId: activeItemId, property, type: mgr.type },
  });
  const required = isRequired({
    platform,
    mgr,
    appUid,
    pageId,
    itemId: activeItemId,
    required: def.required,
  });

  const val =
    value && value.length > 2000
      ? `Script too large! - ${value.length} characters
Click the edit icon to view/edit.`
      : value ?? '';

  return (
    <>
      <TextField
        disabled={isViewOnlyUser}
        data-testid={property}
        className={classes.root}
        fullWidth
        label={def.label}
        variant="standard"
        required={required}
        spellCheck={false}
        value={val}
        multiline
        minRows={5}
        maxRows={15}
        onFocus={(e) => {
          e.target.select();
        }}
        classes={{ root: classes.input }}
        InputProps={{
          startAdornment: (
            <InputAdornment
              sx={{ cursor: 'pointer' }}
              onClick={() => {
                setEdit(true);
              }}
              position="start"
            >
              <Icon iconName="edit" data-tip="Edit" />
            </InputAdornment>
          ),
          endAdornment: helpText ? (
            <InputAdornment position="end">
              <Icon
                className={classes.tip}
                iconName="comment-exclamation"
                data-tip={helpText}
                data-tip-delay={0}
                data-tip-at="top"
              />
            </InputAdornment>
          ) : undefined,
        }}
      />
      {edit ? (
        <TypescriptDialog
          label={def.label}
          value={value || def.defaultScript}
          onSubmit={(_val, editing = false) => {
            onChange(property, _val, true);
            setEdit(editing);
          }}
          onClose={() => {
            setEdit(false);
          }}
          preCompile={def.preCompile}
          compileOnChange={def.compileOnChange}
        />
      ) : null}
    </>
  );
}

export default memo(TypescriptProperty);
